import type { HugeiconsProps } from '@hugeicons/react-native';
import type { TFunction } from 'i18next';

import { getEventListItems } from '~/components/domain/events/eventListItems';
import { getPhotoListItems } from '~/components/domain/photos/photoListItems';
import {
  eventListTypes,
  photoListTypes,
  type EventListType,
  type PhotoListType,
} from '~/constants/lists';

export type DiscoverListType = `photos:${PhotoListType}` | `events:${EventListType}`;

export interface IDiscoverListItem {
  value: DiscoverListType;
  label: string;
  icon: HugeiconsProps['icon'];
  solidIcon: HugeiconsProps['icon'];
  href: string;
  sectionTitleBefore?: string;
  separatorBefore?: boolean;
}

export function getDiscoverListHref(value: DiscoverListType): string {
  const [section, type] = value.split(':');
  return `/${section}/${type}`;
}

export function getDiscoverListTypeFromPathname(
  pathname?: string | null
): DiscoverListType | null {
  if (!pathname) return null;

  const photoMatch = photoListTypes.find((type) => pathname.startsWith(`/photos/${type}`));
  if (photoMatch) return `photos:${photoMatch}`;

  const eventMatch = eventListTypes.find((type) => pathname.startsWith(`/events/${type}`));
  if (eventMatch) return `events:${eventMatch}`;

  return null;
}

export function getDiscoverListItems(t: TFunction): IDiscoverListItem[] {
  const photoItems: IDiscoverListItem[] = getPhotoListItems(t).map((item, index) => ({
    value: `photos:${item.value}`,
    label: item.label,
    icon: item.icon,
    solidIcon: item.solidIcon,
    href: item.href,
    sectionTitleBefore: index === 0 ? t('navigation.photos') : undefined,
  }));

  const eventItems: IDiscoverListItem[] = getEventListItems(t).map((item, index) => ({
    value: `events:${item.value}`,
    label: item.label,
    icon: item.icon,
    solidIcon: item.solidIcon,
    href: item.href,
    sectionTitleBefore: index === 0 ? t('navigation.events') : undefined,
    separatorBefore: index === 0,
  }));

  return [...photoItems, ...eventItems];
}
